import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl
} from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface SaveLayoutModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (layoutName: string, isRename: boolean) => Promise<void>;
  existingNames: string[];
  currentLayoutName?: string | null;
}

type SaveMode = "update" | "saveAs";

export function SaveLayoutModal({
  open,
  onClose,
  onSave,
  existingNames,
  currentLayoutName
}: SaveLayoutModalProps) {
  const { t } = useTranslation();
  const [layoutName, setLayoutName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMode, setSaveMode] = useState<SaveMode>("update");
  const isUpdating = !!currentLayoutName;

  // Initialize with current layout name when editing
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (open) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setLayoutName(currentLayoutName || "");
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setError(null);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-derived-state
      setSaveMode(currentLayoutName ? "update" : "saveAs");
    }
  }, [open, currentLayoutName]);

  const handleSave = async () => {
    // Validate name
    if (!layoutName.trim()) {
      setError(t("layout name is required"));
      return;
    }

    // Check for duplicates based on mode
    if (
      saveMode === "saveAs" ||
      (isUpdating && layoutName.trim() !== currentLayoutName)
    ) {
      if (existingNames.includes(layoutName.trim())) {
        setError(t("layout name already exists"));
        return;
      }
    }

    try {
      setSaving(true);
      setError(null);
      // isRename is true only when updating an existing layout with a different name
      const isRename =
        saveMode === "update" &&
        !!currentLayoutName &&
        layoutName.trim() !== currentLayoutName;
      await onSave(layoutName.trim(), isRename);
      setLayoutName("");
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed to save layout"));
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setLayoutName("");
      setError(null);
      onClose();
    }
  };

  const getButtonText = () => {
    if (saving) return t("saving...");
    if (saveMode === "update" && currentLayoutName) {
      return t("update");
    }
    return t("save as new");
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("save")}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {isUpdating && (
            <FormControl component="fieldset" sx={{ mb: 2, width: "100%" }}>
              <RadioGroup
                value={saveMode}
                onChange={(e) => {
                  const newMode = e.target.value as SaveMode;
                  setSaveMode(newMode);
                  setError(null);
                  // When switching to "save as", clear the name to prompt for new one
                  if (newMode === "saveAs") {
                    setLayoutName("");
                  } else {
                    setLayoutName(currentLayoutName || "");
                  }
                }}
              >
                <FormControlLabel
                  value="update"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {t("update existing layout")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("save changes to")} "{currentLayoutName}"
                      </Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="saveAs"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">
                        {t("save as new layout")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t("create a new layout with a different name")}
                      </Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {(!isUpdating || saveMode === "saveAs") && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("enter a name for this layout")}
            </Typography>
          )}

          {/* Show text field for update mode or always for save as */}
          {(!isUpdating || saveMode === "saveAs" || saveMode === "update") && (
            <TextField
              autoFocus={!isUpdating || saveMode === "saveAs"}
              fullWidth
              label={t("layout name")}
              value={layoutName}
              onChange={(e) => {
                setLayoutName(e.target.value);
                setError(null);
              }}
              error={!!error}
              helperText={error}
              disabled={saving || (isUpdating && saveMode === "update")}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !saving) {
                  void handleSave();
                }
              }}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ gap: 1, px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={saving} color="inherit">
          {t("cancel")}
        </Button>
        <Button
          onClick={() => void handleSave()}
          variant="contained"
          color="primary"
          disabled={saving || !layoutName.trim()}
        >
          {getButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
